import React, {
  useContext,
  useState,
  useRef,
  useEffect,
  // useCallback,
} from 'react';
import classNames from 'classnames';
import styled from 'styled-components';
import * as _ from 'lodash';
import PropTypes from 'prop-types';
import { DragSource as dragSource } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import AC_GRID from '../AutocodeTypes';
import * as cnsts from '../AutocodeConstants';
import AutocodeSelectionContext from '../ACContext/ACSelectionBoxContext';
import AutocodeSelectionAction from './AutocodeSelectionActions';
import {
  ACOperatorBlock,
  ACEndPointBlock,
  ACDefinitionBlock,
  ACFunctionBlock,
} from './AutocodeSelectedNodes';
import { ACSelectionPalette } from './ACSelectionsPalette';
import { useACEditContext } from '../ACContext/ACEditContext';

const DivItem = styled.div`
  box-sizing: border-box;
  border-radius: 3px;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 17.7px;
  padding: 2px 4px;
  margin: 1px;
  cursor: pointer;
  background: ${props =>
    props.selected && props.oneBelowRoot
      ? ACSelectionPalette[props.group].background
      : 'none'} !important;
  border: 1px dashed
    ${props =>
      props.selected && props.oneBelowRoot
        ? ACSelectionPalette[props.group].border
        : 'white'} !important;
  ${props => (props.commaSeparated ? '&:after{content: ",";};' : '')}
`;

// ${props => (props.commaSeparated ? '&:after{content: "🚀";};' : '')}

const source = {
  beginDrag(props) {
    const selectedNodes = [];
    // array created manually
    selectedNodes.push(...props.selectionboxData);
    return { selectedNodes };
  },
  endDrag(props, monitor) {
    if (monitor.didDrop() && !props.saveDefnActive) {
      props.removeSelectedNodes();
    }
  },
};

const collect = (connect, monitor) => ({
  connectDragSource: connect.dragSource(),
  connectDragPreview: connect.dragPreview(),
  isDragging: monitor.isDragging(),
});

const AutocodeSelectionBoxItems = props => {
  const {
    connectDragSource,
    selectionBoxRef,
    definitionTab,
    setGroupWithOn,
    handleDeleteClicked,
    saveDefnActive,
    selEditLabelBoxactive,
    infoBoxRef,
    selectedOpNode,
    tempShowKeywords,
    tempShowKeywordsInCodingBox,
  } = props;
  const [selectedNodeId, setSelectedNodeId] = useState('');
  const [nodesCount, setNodesCount] = useState(0);
  const [acSelectionPrevState, setAcSelectionPrevState] = useState([]);
  const selBox = useRef(null);
  const acSelContext = useContext(AutocodeSelectionContext);
  const {
    selectAllNodes,
    setNodeSelectionById,
    duplicateSelectedNodes,
    copySelectedNodes,
    pasteCopiedNodes,
    selectAllOperators,
    changeSelectedOperatorById,
    selectAllFunctions,
    selectFunctionById,
  } = AutocodeSelectionAction(acSelContext.state, acSelContext.dispatch);

  const { editMode, createDefMode, toggleDragDrop } = useACEditContext();

  const handleMouseOver = (event, node) => {
    if (
      event.button === 0 &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !node.selected
    ) {
      const selected = node.selected;
      if (node.rootnodeChild !== cnsts.AC_NODETYPE_ROOT) return;
      setGroupWithOn(false);
      selectAllNodes(false);
      selectAllOperators(false);
      selectAllFunctions(false);
      const index = _.findIndex(
        acSelContext.state.rootNode.children,
        n => n.id === node.id
      );
      if (selectedNodeId === '') setSelectedNodeId(node.id);
      setNodeSelectionById(node.id, !selected);
      setSelectedNodeId(node.id);
    }
  };

  const handleMouseUp = (e, node) => {
    if (e.button === 0) {
      selectAllOperators(false);
      selectAllFunctions(false);
      const index = _.findIndex(
        acSelContext.state.rootNode.children,
        n => n.id === node.id
      );
      if (e.ctrlKey) {
        setGroupWithOn(false);
        selectAllNodes(false);
        return acSelContext.state.rootNode.children.forEach((n, i) => {
          if (node.id === n.id || n.selected) {
            setNodeSelectionById(n.id, true);
          }
        });
      }
      if (selectedNodeId === '') setSelectedNodeId(node.id);
      if (e.shiftKey) {
        let sIndex = _.findIndex(
          acSelContext.state.rootNode.children,
          n => n.id === selectedNodeId
        );
        sIndex = sIndex === -1 ? index : sIndex;

        if (
          sIndex >= 0 &&
          sIndex < acSelContext.state.rootNode.children.length
        ) {
          const minIndex = Math.min(index, sIndex);
          const maxIndex = Math.max(index, sIndex);
          return acSelContext.state.rootNode.children.forEach((n, i) => {
            if (i >= minIndex && i <= maxIndex) {
              setNodeSelectionById(n.id, true);
            }
          });
        }
      }
    }
    const allSelectedNode = [];
    acSelContext.state.rootNode.children.forEach((n, i) => {
      if (n.selected) {
        allSelectedNode.push(n);
      }
    });
    if (allSelectedNode.length > 1) {
      return acSelContext.state.rootNode.children.forEach((n, i) => {
        setNodeSelectionById(n.id, false);
      });
    }
    setNodeSelectionById(node.id, true);
    setSelectedNodeId(node.id);
    if (infoBoxRef.current.props.isOpen)
      infoBoxRef.current.targetRef.current.click();
    e.stopPropagation();
    return undefined;
  };

  const handleFunctionSelection = (event, node) => {
    const toggle = !node.data.selected;
    setGroupWithOn(false);
    selectAllNodes(false);
    selectAllOperators(false);
    setSelectedNodeId('');

    const isCtrlKey = event.metaKey || event.ctrlKey;
    if (!isCtrlKey) {
      selectAllFunctions(false);
    }
    selectFunctionById(node.id, toggle);
    if (infoBoxRef.current.props.isOpen)
      infoBoxRef.current.targetRef.current.click();
    event.stopPropagation();
  };

  const clearSelections = () => {
    setGroupWithOn(false);
    selectAllNodes(false);
    selectAllFunctions(false);
    selectAllOperators(false);
    setSelectedNodeId('');
  };

  const handleOperatorSelections = (event, node) => {
    clearSelections();
    selectedOpNode(node);

    // this code craps out so added check for null
    if (infoBoxRef && infoBoxRef.current && infoBoxRef.current.props.isOpen)
      infoBoxRef.current.targetRef.current.click();
    changeSelectedOperatorById(node.data.type, node.id, !node.data.selected);
  };

  const handleBoxClick = (e, selectionBox) => {
    const isCtrlKey = e.metaKey || e.ctrlKey;
    if (!isCtrlKey && selectionBox) {
      // clearSelections();
      if (infoBoxRef.current.props.isOpen)
        infoBoxRef.current.targetRef.current.click();
      e.stopPropagation();
    }
  };

  const keyHandler = e => {
    // for Mac users check metakey
    const isCtrlKey = e.metaKey || e.ctrlKey;

    switch (_.lowerCase(e.key)) {
      default:
        break;
      case 'delete':
      case 'backspace': {
        // backspace for MACs
        handleDeleteClicked();
        break;
      }
      case 'a': {
        if (isCtrlKey) selectAllNodes(true);
        break;
      }
      case 'c': {
        if (isCtrlKey) copySelectedNodes();
        break;
      }
      case 'd': {
        if (isCtrlKey) duplicateSelectedNodes();
        break;
      }
      case 'v': {
        if (isCtrlKey) pasteCopiedNodes();
        break;
      }
      case 'k': {
        if (isCtrlKey) tempShowKeywords();
        break;
      }
      case 'm': {
        if (isCtrlKey) tempShowKeywordsInCodingBox();
        break;
      }
    }

    e.preventDefault();
  };

  useEffect(() => {
    props.connectDragPreview(getEmptyImage(), {
      captureDraggingState: true,
    });
  }, []);

  useEffect(() => {
    // if the list grows then scroll to the bottom
    if (nodesCount !== acSelContext.state.rootNode.children.length) {
      setTimeout(() => {
        if (selBox && selBox.current)
          selBox.current.scrollIntoView({ behavior: 'smooth' });
      }, 500);
      setGroupWithOn(false);
      if (
        acSelContext.state.rootNode.children.filter(x => x.selected).length ===
        0
      ) {
        setSelectedNodeId('');
      }
    }
    if (
      nodesCount === acSelContext.state.rootNode.children.length &&
      nodesCount > 1 &&
      !_.isEqual(
        acSelectionPrevState,
        acSelContext.state.rootNode.children.map(i => i.type)
      )
    ) {
      setTimeout(() => {
        if (selBox && selBox.current)
          selBox.current.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }

    setNodesCount(acSelContext.state.rootNode.children.length);
    setAcSelectionPrevState(
      acSelContext.state.rootNode.children.map(item => item.type)
    );
  }, [acSelContext.state.rootNode]);

  const drawNodeHeirarchy = (
    topNode,
    lastNodeInArray,
    commaSeparated = false,
    showTimePeriod = false
  ) => {
    const drawEachNode = node => {
      let output = <React.Fragment />;

      switch (node.type) {
        default:
          break;
        case cnsts.AC_NODETYPE_DATAPOINT: {
          output = (
            <ACEndPointBlock
              node={node}
              defParts={acSelContext.state.partsToShow}
              showTimePeriod={showTimePeriod}
              handleMouseOver={handleMouseOver}
              handleMouseUp={handleMouseUp}
            />
          );
          break;
        }
        case cnsts.AC_NODETYPE_OPERATOR: {
          output = (
            <ACOperatorBlock
              rootNode={node}
              handleOpClick={handleOperatorSelections}
              drawNodeHeirarchy={drawNodeHeirarchy}
              defParts={acSelContext.state.partsToShow}
              showTimePeriod={showTimePeriod}
              handleMouseOver={handleMouseOver}
              handleMouseUp={handleMouseUp}
            />
          );
          break;
        }
        case cnsts.AC_NODETYPE_DEFINITION: {
          output = (
            <ACDefinitionBlock
              node={node}
              handleMouseOver={handleMouseOver}
              handleMouseUp={handleMouseUp}
            />
          );
          break;
        }
        case cnsts.AC_NODETYPE_FUNC: {
          output = (
            <ACFunctionBlock
              node={node}
              drawNodeHeirarchy={drawNodeHeirarchy}
              handleFuncClick={handleFunctionSelection}
              handleMouseOver={handleMouseOver}
              handleMouseUp={handleMouseUp}
            />
          );
          break;
        }
        case cnsts.AC_NODETYPE_BRANGE: {
          // special case nodes will be handled
          break;
        }
      }

      return output;
    };

    return (
      <DivItem
        ref={lastNodeInArray ? selBox : null}
        key={topNode.id}
        className={topNode.selected === true ? '' : 'ac-rowHover'}
        selected={topNode.selected}
        group={topNode.group >= ACSelectionPalette.length ? 0 : topNode.group}
        commaSeparated={commaSeparated}
        oneBelowRoot={topNode.rootnodeChild === cnsts.AC_NODETYPE_ROOT}
      >
        {drawEachNode(topNode)}
      </DivItem>
    );
  };

  const hasChildren =
    acSelContext.state &&
    acSelContext.state.rootNode &&
    acSelContext.state.rootNode.children &&
    acSelContext.state.rootNode.children.length > 0;

  const renderMainSelectionBoxItems = () => {
    return (
      <div
        data-test="ACSelectionBoxItems"
        style={
          definitionTab
            ? {
                height: selEditLabelBoxactive
                  ? 'calc(100vh - 370px)'
                  : 'calc(100vh - 335px)',
              }
            : {
                height: saveDefnActive
                  ? selEditLabelBoxactive
                    ? 'calc(100vh - 392px)'
                    : 'calc(100vh - 357px)'
                  : selEditLabelBoxactive
                  ? 'calc(100vh - 420px)'
                  : 'calc(100vh - 387px)',
              }
        }
        className={classNames('ac-selection-box-content-box')}
        onClick={e => handleBoxClick(e)}
        tabIndex="-1"
        onKeyDown={e => keyHandler(e)}
        ref={selectionBoxRef}
      >
        {!hasChildren ? (
          <div className="warning-msg">
            Start by selecting categories and definitions on the left. All added
            items will appear in this section.
          </div>
        ) : (
          <React.Fragment>
            {acSelContext.state.rootNode.children.map(node =>
              drawNodeHeirarchy(node, true, false)
            )}
          </React.Fragment>
        )}
      </div>
    );
  };

  return toggleDragDrop
    ? editMode || createDefMode
      ? renderMainSelectionBoxItems()
      : connectDragSource(renderMainSelectionBoxItems())
    : renderMainSelectionBoxItems();
};

AutocodeSelectionBoxItems.propTypes = {
  selectionBoxRef: PropTypes.shape({}),
  definitionTab: PropTypes.bool,
  setGroupWithOn: PropTypes.func,
  handleDeleteClicked: PropTypes.func,
  selectionboxData: PropTypes.shape([]),
  saveDefnActive: PropTypes.bool,
  selEditLabelBoxactive: PropTypes.bool,
  infoBoxRef: PropTypes.shape({
    current: PropTypes.shape({
      props: PropTypes.shape({
        isOpen: PropTypes.bool,
      }),
      targetRef: PropTypes.shape({
        current: PropTypes.shape({
          click: PropTypes.func,
        }),
      }),
    }),
  }),
  selectedOpNode: PropTypes.func,
};

export default dragSource(
  AC_GRID.AC_GRID_2,
  source,
  collect
)(AutocodeSelectionBoxItems);
